(function () {
    if (window.__JBDataLayerInitialized) return;
    window.__JBDataLayerInitialized = true;

    // Theme-level tracking hub for GTM/GA4-friendly ecommerce events.
    const context = window.__JBDataLayerContext || {};
    const CART_EVENT_NAMES = [
        'action:cart:updateCart',
        'action:cart:updateCartandShowCart',
        'action:showCartPopup',
    ];

    const state = {
        currency: context.currency || 'USD',
        cartSnapshot: null,
        cartSyncQueue: Promise.resolve(),
        pushedSignatures: new Set(),
        pushedPurchases: new Set(),
        viewedPromotions: new Set(),
        lastViewCartAt: 0,
        lastBeginCheckoutAt: 0,
    };

    const dataLayer = (window.dataLayer = window.dataLayer || []);
    window.ShopifyDataLayer = window.ShopifyDataLayer || {};

    function getCartRoot() {
        return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
    }

    function toNumber(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    function roundToTwo(value) {
        return Math.round(toNumber(value) * 100) / 100;
    }

    function cleanupObject(data) {
        const cleaned = {};
        Object.keys(data).forEach((key) => {
            const value = data[key];
            if (value === undefined || value === null || value === '') return;
            if (typeof value === 'number' && Number.isNaN(value)) return;
            cleaned[key] = value;
        });
        return cleaned;
    }

    function pushEcommerceEvent(eventName, ecommercePayload, metadata = {}) {
        if (!eventName || !ecommercePayload) return;
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
            event: eventName,
            ecommerce: ecommercePayload,
            ...metadata,
        });
    }

    function normalizeVariantTitle(title) {
        if (!title || title === 'Default Title') return undefined;
        return title;
    }

    function normalizeCartSnapshot(rawCart) {
        if (!rawCart) return null;
        const rawItems = Array.isArray(rawCart.items) ? rawCart.items : [];
        const currency =
            rawCart.currency ||
            rawCart.currency_code ||
            context.currency ||
            state.currency;
        const items = rawItems.map((item) => ({
            key: String(item.key || item.id || item.variant_id || ''),
            variant_id: toNumber(item.variant_id || item.id, 0),
            product_id: toNumber(item.product_id, 0),
            sku: item.sku || '',
            product_title: item.product_title || item.title || '',
            variant_title: item.variant_title || '',
            vendor: item.vendor || '',
            product_type: item.product_type || '',
            quantity: toNumber(item.quantity, 0),
            final_price: toNumber(item.final_price ?? item.price, 0),
            original_price: toNumber(item.original_price ?? item.final_price ?? item.price, 0),
        }));

        return {
            currency: currency,
            item_count: toNumber(rawCart.item_count, items.reduce((total, item) => total + item.quantity, 0)),
            total_price: toNumber(
                rawCart.total_price,
                items.reduce((total, item) => total + item.final_price * item.quantity, 0)
            ),
            items: items,
        };
    }

    function mapCartLineToGAItem(lineItem, overrides = {}) {
        const quantity = toNumber(overrides.quantity, toNumber(lineItem.quantity, 0));
        const unitPriceCents = toNumber(lineItem.final_price, 0);
        const originalPriceCents = toNumber(lineItem.original_price, unitPriceCents);
        const unitDiscount = Math.max(0, (originalPriceCents - unitPriceCents) / 100);

        return cleanupObject({
            item_id: String(lineItem.variant_id || lineItem.product_id || ''),
            item_name: lineItem.product_title,
            item_brand: lineItem.vendor,
            item_category: lineItem.product_type,
            item_variant: normalizeVariantTitle(lineItem.variant_title),
            price: roundToTwo(unitPriceCents / 100),
            quantity: quantity,
            discount: roundToTwo(unitDiscount),
            coupon: overrides.coupon,
            index: overrides.index,
            item_list_name: overrides.item_list_name,
            item_list_id: overrides.item_list_id,
            sku: lineItem.sku || undefined,
        });
    }

    function calculateItemsValue(items) {
        return roundToTwo(
            items.reduce((total, item) => {
                return total + toNumber(item.price) * toNumber(item.quantity, 1);
            }, 0)
        );
    }

    function buildCartItemsForGA(cartSnapshot, extra = {}) {
        return (cartSnapshot?.items || []).map((lineItem, index) =>
            mapCartLineToGAItem(lineItem, {
                ...extra,
                index: index + 1,
            })
        );
    }

    function mapCartByKey(cartSnapshot) {
        const map = new Map();
        (cartSnapshot?.items || []).forEach((item) => {
            map.set(item.key, item);
        });
        return map;
    }

    function diffCartSnapshots(previousCart, nextCart) {
        const added = [];
        const removed = [];
        const previousByKey = mapCartByKey(previousCart);
        const nextByKey = mapCartByKey(nextCart);

        nextByKey.forEach((nextItem, key) => {
            const previousItem = previousByKey.get(key);
            const previousQuantity = toNumber(previousItem?.quantity, 0);
            const nextQuantity = toNumber(nextItem?.quantity, 0);

            if (nextQuantity > previousQuantity) {
                added.push({
                    lineItem: nextItem,
                    quantityDelta: nextQuantity - previousQuantity,
                });
            }

            if (previousQuantity > nextQuantity) {
                removed.push({
                    lineItem: previousItem,
                    quantityDelta: previousQuantity - nextQuantity,
                });
            }
        });

        previousByKey.forEach((previousItem, key) => {
            if (!nextByKey.has(key)) {
                removed.push({
                    lineItem: previousItem,
                    quantityDelta: toNumber(previousItem.quantity, 0),
                });
            }
        });

        return { added, removed };
    }

    async function fetchCartSnapshot() {
        const response = await fetch(`${getCartRoot()}cart.js`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
        });
        if (!response.ok) return null;
        const rawCart = await response.json();
        return normalizeCartSnapshot(rawCart);
    }

    function trackCartDiff(previousCart, nextCart, source) {
        if (!previousCart || !nextCart) return;
        const { added, removed } = diffCartSnapshots(previousCart, nextCart);

        if (added.length > 0) {
            const addedItems = added.map((entry) =>
                mapCartLineToGAItem(entry.lineItem, { quantity: entry.quantityDelta })
            );
            pushEcommerceEvent(
                'add_to_cart',
                {
                    currency: nextCart.currency || state.currency,
                    value: calculateItemsValue(addedItems),
                    items: addedItems,
                },
                { event_source: source }
            );
        }

        if (removed.length > 0) {
            const removedItems = removed.map((entry) =>
                mapCartLineToGAItem(entry.lineItem, { quantity: entry.quantityDelta })
            );
            pushEcommerceEvent(
                'remove_from_cart',
                {
                    currency: nextCart.currency || state.currency,
                    value: calculateItemsValue(removedItems),
                    items: removedItems,
                },
                { event_source: source }
            );
        }
    }

    function queueCartSync(source) {
        state.cartSyncQueue = state.cartSyncQueue
            .then(async () => {
                const nextCartSnapshot = await fetchCartSnapshot();
                if (!nextCartSnapshot) return;
                if (state.cartSnapshot && source !== 'bootstrap-sync') {
                    trackCartDiff(state.cartSnapshot, nextCartSnapshot, source);
                }
                state.cartSnapshot = nextCartSnapshot;
                state.currency = nextCartSnapshot.currency || state.currency;
            })
            .catch(() => {
                return null;
            });

        return state.cartSyncQueue;
    }

    function trackViewItemEvent() {
        if (!context.product || !context.product.item) return;
        const signature = `view_item|${context.product.item.item_id}|${location.pathname}`;
        if (state.pushedSignatures.has(signature)) return;
        state.pushedSignatures.add(signature);

        const item = cleanupObject(context.product.item);
        pushEcommerceEvent('view_item', {
            currency: context.currency || state.currency,
            value: roundToTwo(toNumber(item.price)),
            items: [item],
        });
    }

    function mapCardToGAItem(card, index, listContainer) {
        const item = cleanupObject({
            item_id: card.dataset.analyticsItemId,
            item_name: card.dataset.analyticsItemName,
            item_brand: card.dataset.analyticsItemBrand,
            item_category: card.dataset.analyticsItemCategory,
            item_variant: normalizeVariantTitle(card.dataset.analyticsItemVariant),
            price: roundToTwo(toNumber(card.dataset.analyticsPrice)),
            quantity: 1,
            discount: roundToTwo(toNumber(card.dataset.analyticsDiscount)),
            item_list_name: listContainer?.dataset.analyticsItemList,
            item_list_id: listContainer?.dataset.analyticsListId,
            index: index,
            sku: card.dataset.analyticsSku,
        });

        if (!item.item_id || !item.item_name) return null;
        return item;
    }

    function trackViewItemListEvents() {
        const listContainers = document.querySelectorAll('[data-analytics-item-list]');
        listContainers.forEach((listContainer) => {
            const cards = Array.from(
                listContainer.querySelectorAll('product-card[data-analytics-item-id]')
            );
            if (!cards.length) return;

            const items = cards
                .map((card, index) => mapCardToGAItem(card, index + 1, listContainer))
                .filter(Boolean);
            if (!items.length) return;

            const signature = [
                'view_item_list',
                listContainer.dataset.analyticsListId || listContainer.dataset.analyticsItemList || 'list',
                location.pathname,
                location.search,
                items.map((item) => item.item_id).join(','),
            ].join('|');

            if (state.pushedSignatures.has(signature)) return;
            state.pushedSignatures.add(signature);

            pushEcommerceEvent('view_item_list', {
                item_list_name: listContainer.dataset.analyticsItemList,
                item_list_id: listContainer.dataset.analyticsListId,
                currency: context.currency || state.currency,
                items: items,
            });
        });
    }

    function trackSelectItemEvents() {
        document.addEventListener(
            'click',
            (event) => {
                const link = event.target.closest('a[data-analytics-product-link]');
                if (!link) return;
                if (event.defaultPrevented) return;

                const clickedControl = event.target.closest(
                    'button, input, select, textarea, label, form, [role="button"], [data-no-analytics-select-item]'
                );
                if (clickedControl && link.contains(clickedControl)) return;

                const card = link.closest('product-card[data-analytics-item-id]');
                if (!card) return;

                const listContainer = card.closest('[data-analytics-item-list]');
                if (!listContainer) return;
                const cardsInList = listContainer
                    ? Array.from(
                          listContainer.querySelectorAll('product-card[data-analytics-item-id]')
                      )
                    : [];
                const index = cardsInList.length ? cardsInList.indexOf(card) + 1 : undefined;
                const item = mapCardToGAItem(card, index, listContainer);
                if (!item) return;

                pushEcommerceEvent('select_item', {
                    item_list_name: listContainer?.dataset.analyticsItemList,
                    item_list_id: listContainer?.dataset.analyticsListId,
                    items: [item],
                });
            },
            true
        );
    }

    function trackSearchEvent() {
        const searchTermsEl = document.querySelector('[data-analytics-search-terms]');
        const fromDomTerms = searchTermsEl?.dataset.analyticsSearchTerms;
        const fromDomCount = searchTermsEl?.dataset.analyticsSearchResults;
        const fromContext = context.search;
        const terms =
            (fromContext && fromContext.performed && fromContext.terms) ||
            fromDomTerms ||
            new URLSearchParams(window.location.search).get('q');

        if (!terms) return;

        const signature = `search|${location.pathname}|${terms}|${location.search}`;
        if (state.pushedSignatures.has(signature)) return;
        state.pushedSignatures.add(signature);

        const resultsCount = toNumber(
            (fromContext && fromContext.results_count) || fromDomCount,
            undefined
        );

        pushEcommerceEvent('search', {
            search_term: terms,
            results_count: resultsCount,
        });
    }

    function getPromotionItemFromElement(promoElement) {
        return cleanupObject({
            promotion_id: promoElement.dataset.analyticsPromoId,
            promotion_name: promoElement.dataset.analyticsPromoName,
            creative_name: promoElement.dataset.analyticsPromoCreative || 'slideshow',
            creative_slot: promoElement.dataset.analyticsPromoSlot,
        });
    }

    function trackPromotionImpressions() {
        const promoElements = document.querySelectorAll('[data-analytics-promo-id]');
        if (!promoElements.length) return;

        const pushViewPromotion = (promoElement) => {
            const promoId = promoElement.dataset.analyticsPromoId;
            if (!promoId || state.viewedPromotions.has(promoId)) return;
            state.viewedPromotions.add(promoId);
            pushEcommerceEvent('view_promotion', {
                items: [getPromotionItemFromElement(promoElement)],
            });
        };

        if (!('IntersectionObserver' in window)) {
            promoElements.forEach(pushViewPromotion);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    pushViewPromotion(entry.target);
                    observer.unobserve(entry.target);
                });
            },
            {
                root: null,
                threshold: 0.5,
            }
        );

        promoElements.forEach((element) => observer.observe(element));
    }

    function trackPromotionClicks() {
        document.addEventListener(
            'click',
            (event) => {
                const link = event.target.closest('[data-analytics-promo-id] a[href]');
                if (!link) return;
                const promoElement = link.closest('[data-analytics-promo-id]');
                if (!promoElement) return;

                pushEcommerceEvent('select_promotion', {
                    items: [getPromotionItemFromElement(promoElement)],
                });
            },
            true
        );
    }

    function trackViewCart(source) {
        const now = Date.now();
        if (now - state.lastViewCartAt < 750) return;
        state.lastViewCartAt = now;

        const items = buildCartItemsForGA(state.cartSnapshot || { items: [] });
        pushEcommerceEvent(
            'view_cart',
            {
                currency: state.cartSnapshot?.currency || state.currency,
                value: calculateItemsValue(items),
                items: items,
            },
            { event_source: source }
        );
    }

    function trackBeginCheckout(source) {
        const now = Date.now();
        if (now - state.lastBeginCheckoutAt < 1000) return;
        state.lastBeginCheckoutAt = now;

        const items = buildCartItemsForGA(state.cartSnapshot || { items: [] });
        pushEcommerceEvent(
            'begin_checkout',
            {
                currency: state.cartSnapshot?.currency || state.currency,
                value: calculateItemsValue(items),
                items: items,
            },
            { event_source: source }
        );
    }

    function installCartAndCheckoutListeners() {
        CART_EVENT_NAMES.forEach((eventName) => {
            document.addEventListener(eventName, () => {
                queueCartSync(eventName);
            });
        });

        document.addEventListener(
            'action:showDrawer',
            (event) => {
                if (event?.detail?.drawerType !== 'cart-drawer') return;
                trackViewCart('cart-drawer');
            },
            true
        );

        document.addEventListener(
            'click',
            (event) => {
                const checkoutButton = event.target.closest('[name="checkout"]');
                if (!checkoutButton) return;
                trackBeginCheckout('checkout-button-click');
            },
            true
        );

        document.addEventListener(
            'submit',
            (event) => {
                const submitter = event.submitter;
                if (submitter && submitter.name === 'checkout') {
                    trackBeginCheckout('checkout-form-submit');
                    return;
                }

                const hasCheckoutButton = !!event.target.querySelector('[name="checkout"]');
                if (hasCheckoutButton) {
                    trackBeginCheckout('checkout-form-submit');
                }
            },
            true
        );
    }

    function installListObservers() {
        let listObserverTimer;
        const scheduleListTracking = () => {
            clearTimeout(listObserverTimer);
            listObserverTimer = setTimeout(() => {
                trackViewItemListEvents();
            }, 120);
        };

        const collectionGrid = document.getElementById('collection-grid');
        if (collectionGrid && 'MutationObserver' in window) {
            const observer = new MutationObserver(scheduleListTracking);
            observer.observe(collectionGrid, { childList: true, subtree: true });
        }

        document.addEventListener('action:filter:update', scheduleListTracking);
        window.addEventListener('popstate', scheduleListTracking);
        scheduleListTracking();
    }

    function installPurchaseHook() {
        window.ShopifyDataLayer.pushPurchase = function (purchasePayload) {
            if (!purchasePayload || !purchasePayload.transaction_id) return;
            const transactionId = String(purchasePayload.transaction_id);
            if (state.pushedPurchases.has(transactionId)) return;
            state.pushedPurchases.add(transactionId);
            pushEcommerceEvent('purchase', purchasePayload, {
                event_source: 'manual-purchase-hook',
            });
        };
    }

    function bootstrap() {
        state.cartSnapshot = normalizeCartSnapshot(context.initial_cart);
        if (state.cartSnapshot?.currency) state.currency = state.cartSnapshot.currency;

        trackViewItemEvent();
        trackSearchEvent();
        trackPromotionImpressions();
        trackPromotionClicks();
        trackSelectItemEvents();
        installListObservers();
        installCartAndCheckoutListeners();
        installPurchaseHook();

        if (context.page_type === 'cart') {
            trackViewCart('cart-page');
        }

        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => queueCartSync('bootstrap-sync'), { timeout: 2000 });
        } else {
            setTimeout(() => queueCartSync('bootstrap-sync'), 400);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootstrap);
    } else {
        bootstrap();
    }
})();
